import type { FC } from 'react';
import React, { useMemo } from 'react';
import type { ButtonProps } from '@edreamsodigeo/prisma-design-system';
import {
  BackgroundImage,
  Button,
  Flex,
} from '@edreamsodigeo/prisma-design-system';

import MapCardWrapper from './MapCardWrapper';
import { getResultsPageUrl } from '~utils/HSAUrlHandler';
import { BuyPath, type RoomRequest } from '~types';
import { navigateToRelativePath, openUrlOnNewTab } from '~utils/navigate';
import type { MapCardCtaPosition, Position } from './types';
import { STATIC_MAP_IMAGE_URL } from './mapData';
import AreaPreview from './MapCardAreaPreview';
import {
  DEFAULT_MAP_DRAWING_AREA,
  DEFAULT_RENDERING_OFFSET,
} from './constants';
import { DiscountDot, NormalDot } from './MapCardDots';
import { regenerateAllDots } from './mapCardUtils';

export interface MapCardProps {
  checkInDate: string;
  checkOutDate: string;
  buyPath?: string;
  showDiscountAreas?: boolean;
  showNormalAreas?: boolean;
  showRestrictedAreas?: boolean;
  showOpenedMap?: boolean;
  roomRequests?: RoomRequest[];
  geoId: number;
  mapWidth?: number;
  mapHeight?: number;
  ctaPosition?: MapCardCtaPosition;
  renderingOffset?: Position;
  seedOverride?: string;
  ctaLabel?: string;
  ctaType?: 'secondary' | 'mapHeader';
  trackClick: () => void;
  trackClickRef?: React.MutableRefObject<HTMLDivElement | null>;
  openInSameTab?: boolean;
  shouldKeepQueryParams?: boolean;
  hasBorderRadius?: boolean;
  onMapClick?: () => void; // Optional callback for additional click handling
}

const backgroundImageUrl = `url("${STATIC_MAP_IMAGE_URL}")`;

const asPercentage = (value: number) => `${Math.round(value * 100)}%`;

const offsetPercentage = (offset: Position) =>
  `${asPercentage(offset.x)} ${asPercentage(offset.y)}`;

const MapCard: FC<MapCardProps> = ({
  showDiscountAreas,
  showNormalAreas,
  showRestrictedAreas,
  checkInDate,
  checkOutDate,
  geoId,
  roomRequests,
  mapWidth = DEFAULT_MAP_DRAWING_AREA.w,
  mapHeight = DEFAULT_MAP_DRAWING_AREA.h,
  ctaPosition = 'bottom',
  renderingOffset = DEFAULT_RENDERING_OFFSET,
  seedOverride,
  ctaLabel,
  ctaType = 'secondary',
  buyPath = BuyPath.FlightsResultsHotelSuggestions,
  openInSameTab,
  shouldKeepQueryParams = true,
  hasBorderRadius,
  trackClick,
  trackClickRef,
  onMapClick,
  showOpenedMap = true,
}) => {
  const { discountAreas, normalAreas, restrictedAreas, dots } = useMemo(
    () =>
      regenerateAllDots({
        ctaPosition,
        renderingOffset,
        drawingArea: { w: mapWidth, h: mapHeight },
        seedOverride,
      }),
    [ctaPosition, renderingOffset, mapWidth, mapHeight, seedOverride]
  );

  const handleClick = () => {
    if (trackClick) trackClick();
    if (onMapClick) onMapClick();

    const resultsPageUrl = getResultsPageUrl(
      {
        buyPath,
        checkInDate,
        checkOutDate,
        geoId,
        roomRequests,
        isMapOpened: showOpenedMap ?? true,
      },
      { shouldKeepQueryParams, shouldUseGeoNodeId: true }
    );

    if (openInSameTab) {
      navigateToRelativePath(resultsPageUrl);
    } else {
      openUrlOnNewTab(resultsPageUrl);
    }
  };

  let ctaProps: Omit<ButtonProps, 'ref'> = {
    type: 'secondary',
  };
  if (ctaType === 'mapHeader') {
    ctaProps = {
      type: 'primary',
      sx: {
        backgroundColor: 'primary.90',
      },
    };
  }

  return (
    <MapCardWrapper
      trackingClickRef={trackClickRef}
      dataTestId="accommodations-recommendations-map-card"
      handleClick={handleClick}
      hasBorderRadius={hasBorderRadius}
    >
      <BackgroundImage
        backgroundSize="cover"
        backgroundPosition={offsetPercentage(renderingOffset)}
        backgroundImage={backgroundImageUrl}
        height={`${mapHeight}px`}
      >
        <Flex
          height="100%"
          sx={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 11.18%, rgba(0, 0, 0, 0.16) 100%)',
          }}
          justifyContent={ctaPosition === 'bottom' ? 'flex-end' : 'center'}
          alignItems="center"
          flexDirection="column"
          position="relative"
          padding="1x_small"
        >
          {showDiscountAreas &&
            Object.keys(discountAreas).map((key) => (
              <AreaPreview
                key={`safe-${key}`}
                x={discountAreas[key].x}
                y={discountAreas[key].y}
                w={discountAreas[key].w}
                h={discountAreas[key].h}
                bg="rgba(144, 238, 144, 0.3)"
              />
            ))}
          {showNormalAreas &&
            Object.keys(normalAreas).map((key) => (
              <AreaPreview
                key={`safe-${key}`}
                x={normalAreas[key].x}
                y={normalAreas[key].y}
                w={normalAreas[key].w}
                h={normalAreas[key].h}
                bg="rgba(53,101,251,0.3)"
              />
            ))}
          {showRestrictedAreas &&
            restrictedAreas.map((area, idx) => (
              <AreaPreview
                key={`restricted-${idx}`}
                x={area.x}
                y={area.y}
                w={area.w}
                h={area.h}
                bg="rgba(255, 99, 71, 0.3)"
              />
            ))}
          {/* Dots */}
          {dots.map((dot, idx) => (
            <React.Fragment key={`dot-${idx}`}>
              {dot.discount ? (
                <DiscountDot x={dot.x} y={dot.y} discount={dot.discount} />
              ) : (
                <NormalDot x={dot.x} y={dot.y} />
              )}
            </React.Fragment>
          ))}

          <Button size="medium" {...ctaProps}>
            {ctaLabel}
          </Button>
        </Flex>
      </BackgroundImage>
    </MapCardWrapper>
  );
};

export default MapCard;
